<template>
    <a
        href="#"
        class="ask-pendo-next-actions-link"
        :class="{ 'ask-pendo-next-actions-link--completed': isCompleted }"
        role="button"
        :aria-label="buttonLabel"
        :aria-disabled="isCompleted"
        @click.prevent="handleClick"
        @keydown.enter.prevent="handleClick"
        @keydown.space.prevent="handleClick">
        <pendo-icon
            v-if="isCompleted"
            class="ask-pendo-next-actions-link__checkmark"
            type="check-circle-2-solid"
            fill="var(--palette-teal-90)"
            stroke="var(--palette-gray-0)" />
        <span class="ask-pendo-next-actions-link__text">
            {{ buttonLabel }}
        </span>
    </a>
</template>

<script>
import { PendoIcon } from '@pendo/components';

export default {
    name: 'AskPendoNextActionsLink',
    components: {
        PendoIcon,
    },
    props: {
        artifact: {
            type: Object,
            required: true,
        },
    },
    emits: ['click'],
    computed: {
        isCompleted () {
            return this.artifact?.data?.completed === true;
        },
        buttonLabel () {
            const { action_type: actionType, data = {}, name } = this.artifact;

            // Ask question actions use the question as the label
            if (actionType === 'ask_question') {
                return data.question;
            }

            // Non-segment actions use default
            if (actionType !== 'create_segment') {
                return this.getDefaultActionLabel();
            }

            // No type available → fallback
            if (!data.type) {
                return this.$t('askPendo.nextActionLink.Create segment');
            }

            // Named segment
            if (name) {
                // For page/feature/track_event, add "visitors" prefix
                if (['page', 'feature', 'trackEvent'].includes(data.type)) {
                    return this.$t('askPendo.nextActionLink.Create a segment of visitors who {name}', { name });
                }

                return this.$t('askPendo.nextActionLink.Create segment for {name}', { name });
            }

            // ID-based fallback (visitor/account)
            if (['visitor', 'account'].includes(data.type)) {
                return this.getIdBasedSegmentLabel(data);
            }

            // Generic fallback
            return this.$t('askPendo.nextActionLink.Create segment');
        },
    },
    methods: {
        getIdBasedSegmentLabel (data) {
            const count = data.type === 'visitor'
                ? (data.visitorIds?.length || 0)
                : (data.accountIds?.length || 0);

            // Handle empty arrays - fall back to default label
            if (count === 0) {
                return this.$t('askPendo.nextActionLink.Create segment');
            }

            // Get the entity type in singular or plural form
            if (data.type === 'visitor') {
                return count === 1
                    ? this.$t('askPendo.nextActionLink.Create a new segment with this visitor')
                    : this.$t('askPendo.nextActionLink.Create a new segment with these {count} visitors', { count });
            }

            return count === 1
                ? this.$t('askPendo.nextActionLink.Create a new segment with this account')
                : this.$t('askPendo.nextActionLink.Create a new segment with these {count} accounts', { count });
        },
        getDefaultActionLabel () {
            // Default labels for other action types
            const actionLabels = {
                create_segment: this.$t('askPendo.nextActionLink.Create segment'),
                create_guide: this.$t('askPendo.nextActionLink.Create guide'),
                create_report: this.$t('askPendo.nextActionLink.Create report'),
                view_details: this.$t('askPendo.nextActionLink.View details'),
            };

            return actionLabels[this.artifact.action_type] || this.$t('askPendo.nextActionLink.Take action');
        },
        handleClick () {
            if (this.isCompleted) {
                return;
            }

            this.$emit('click', this.artifact);
        },
    },
};
</script>

<style scoped lang="less">
.ask-pendo-next-actions-link {
    display: flex;
    align-items: center;
    gap: 8px;
    color: var(--palette-teal-90);
    text-decoration: none;
    cursor: pointer;
    font-size: 16px;
    line-height: 28px;
    position: relative;

    &:hover {
        color: var(--palette-teal-80);
    }

    &--completed {
        cursor: text;

        .ask-pendo-next-actions-link__text {
            text-decoration: line-through;
            color: var(--palette-teal-90);
        }
    }

    &__checkmark {
        width: 18px;
        height: 18px;
        flex-shrink: 0;
        position: absolute;
        left:-22px;
        top: 5px;
    }

    &__text {
        flex: 1;
    }
}

// Hide the list number when the item is completed
:global(li:has(.ask-pendo-next-actions-link--completed)) {
    list-style: none;
}
</style>

